import type { MarketCode } from "./localization";

export type TopicResourceLink = {
  label: string;
  to: string;
  description: string;
};

export type TopicSourceLink = {
  label: string;
  url: string;
  note: string;
};

export type SeoTopicCluster = {
  slug: string;
  shortTitle: string;
  title: string;
  marketCodes: MarketCode[];
  marketLabel: string;
  category: string;
  metaDescription: string;
  summary: string;
  searchIntents: string[];
  calculatorLinks: TopicResourceLink[];
  guideLinks: TopicResourceLink[];
  sections: Array<{
    title: string;
    body: string;
  }>;
  checklist: string[];
  faqs: Array<{
    question: string;
    answer: string;
  }>;
  sources: TopicSourceLink[];
};

export const SEO_TOPIC_CLUSTERS: SeoTopicCluster[] = [
  {
    slug: "us-paycheck-tax-401k-take-home-pay",
    shortTitle: "US Paycheck and 401(k)",
    title: "US Paycheck Tax, 401(k), and Take-Home Pay Planning Hub",
    marketCodes: ["US"],
    marketLabel: "United States",
    category: "Tax and Payroll",
    metaDescription:
      "Plan US take-home pay with federal income tax, FICA, 401(k) contributions, bonus withholding, salary changes, and monthly budget pressure.",
    summary:
      "This hub helps US readers answer one practical question: how much of gross pay can actually be used after tax, payroll deductions, retirement contributions, debt payments, and required household costs?",
    searchIntents: [
      "US paycheck after tax and 401k contribution",
      "federal income tax calculator with take home pay",
      "how 401k contribution changes net paycheck",
      "US salary after FICA and federal tax",
    ],
    calculatorLinks: [
      {
        label: "US Income Tax Calculator",
        to: "/us/tools/income-tax-calculator",
        description: "Estimate federal income tax and net pay from filing status, income, and deduction assumptions.",
      },
      {
        label: "US 401(k) Calculator",
        to: "/us/tools/us-401k-calculator",
        description: "Model employee contributions, employer match, projected growth, and early withdrawal penalties.",
      },
      {
        label: "Bonus Tax Calculator",
        to: "/tools/bonus-tax-calculator",
        description: "Compare supplemental wage withholding with the cash a bonus may actually add to the budget.",
      },
      {
        label: "Budget Calculator",
        to: "/tools/budget-calculator",
        description: "Turn net pay into housing, debt, saving, transport, insurance, and monthly spending capacity.",
      },
    ],
    guideLinks: [
      {
        label: "US Paycheck Tax Withholding and 401(k) Guide",
        to: "/us/guides/us-paycheck-tax-withholding-401k-guide",
        description: "Detailed US guide for withholding, FICA, retirement contributions, and paycheck planning.",
      },
      {
        label: "US 401(k) Match Guide",
        to: "/us/guides/us-401k-match-contribution-guide",
        description: "Explains employer match, vesting, Roth versus traditional treatment, and annual limits.",
      },
      {
        label: "Bonus Tax Withholding Guide",
        to: "/guides/bonus-tax-withholding-guide",
        description: "Shows why bonus pay can feel smaller than expected and how to plan the cash before it arrives.",
      },
    ],
    sections: [
      {
        title: "Why this is the first US cluster to win",
        body:
          "Broad terms like tax calculator are too competitive for a new domain. A narrower paycheck question is more realistic because users search with details such as filing status, 401(k), FICA, bonus withholding, and monthly take-home pay. The page should answer the full workflow, not only the tax formula.",
      },
      {
        title: "The calculation path",
        body:
          "Start with gross income, then separate federal income tax, Social Security and Medicare payroll tax, pre-tax retirement contributions, post-tax savings, health deductions where known, and monthly debt obligations. A paycheck result is useful only when it becomes a budgetable cash number.",
      },
      {
        title: "What makes the page different",
        body:
          "The useful angle is the tradeoff between today and later. A higher 401(k) contribution may lower take-home cash, but it can capture match and improve retirement readiness. A lower contribution may ease monthly pressure, but it can leave employer money and compounding time unused.",
      },
    ],
    checklist: [
      "Use the calculator with annual salary and filing assumptions.",
      "Run a second case with the intended 401(k) contribution.",
      "Compare net monthly pay with rent, debt, insurance, groceries, transport, and emergency savings.",
      "Check official IRS rules before relying on tax-sensitive outputs.",
      "Update the numbers after a raise, bonus, job change, or benefit election.",
    ],
    faqs: [
      {
        question: "Can a new site rank for US paycheck calculator searches without backlinks?",
        answer:
          "It can start with specific long-tail searches, especially if the page connects taxes, 401(k), bonus pay, and household budget in one useful workflow. Broad paycheck terms will still be difficult without external authority.",
      },
      {
        question: "Why link 401(k) planning to take-home pay?",
        answer:
          "Because the contribution affects both current cash flow and long-term retirement savings. Users need to see the tradeoff before choosing a contribution rate.",
      },
    ],
    sources: [
      {
        label: "Internal Revenue Service",
        url: "https://www.irs.gov/filing/federal-income-tax-rates-and-brackets",
        note: "Federal income tax rates and brackets",
      },
      {
        label: "Internal Revenue Service",
        url: "https://www.irs.gov/pub/irs-pdf/p15t.pdf",
        note: "Federal withholding method reference",
      },
      {
        label: "Internal Revenue Service",
        url: "https://www.irs.gov/newsroom/401k-limit-increases-to-24500-for-2026-ira-limit-increases-to-7500",
        note: "2026 retirement contribution limit announcement",
      },
    ],
  },
  {
    slug: "us-credit-card-payoff-dti-mortgage-readiness",
    shortTitle: "US Debt to Mortgage",
    title: "US Credit Card Payoff, DTI, and Mortgage Readiness Hub",
    marketCodes: ["US"],
    marketLabel: "United States",
    category: "Debt and Mortgage",
    metaDescription:
      "Use US credit card payoff, debt-to-income, mortgage affordability, and budget calculators together before applying for a home loan.",
    summary:
      "This hub connects revolving debt payoff with mortgage readiness, because many users search for a debt calculator when the real decision is whether they can qualify for or comfortably carry a housing payment.",
    searchIntents: [
      "credit card payoff before mortgage application",
      "debt to income calculator for home loan",
      "should I pay credit cards before buying a house",
      "US mortgage affordability after debt payoff",
    ],
    calculatorLinks: [
      {
        label: "Credit Cards Payoff Calculator",
        to: "/tools/credit-cards-payoff-calculator",
        description: "Compare payoff dates, interest cost, avalanche strategy, and extra monthly payments.",
      },
      {
        label: "Debt-to-Income Calculator",
        to: "/tools/debt-to-income",
        description: "Estimate back-end DTI from monthly debt obligations and gross income.",
      },
      {
        label: "Home Affordability Calculator",
        to: "/tools/home-affordability-calculator",
        description: "Translate income, debts, down payment, and rate assumptions into a realistic home price range.",
      },
      {
        label: "Mortgage Calculator",
        to: "/tools/mortgage-calculator",
        description: "Estimate principal, interest, taxes, insurance, and monthly housing cost.",
      },
    ],
    guideLinks: [
      {
        label: "US Credit Card Payoff Guide",
        to: "/us/guides/us-credit-card-payoff-apr-minimum-payment-guide",
        description: "APR, minimum payments, avalanche method, documentation, and consumer-rights context.",
      },
      {
        label: "Mortgage Affordability Guide",
        to: "/guides/mortgage-affordability-payment-guide",
        description: "Explains principal, interest, taxes, insurance, reserves, and payment stress tests.",
      },
      {
        label: "Debt-to-Income Ratio Guide",
        to: "/guides/debt-to-income-ratio-borrowing-signal",
        description: "Shows why lender DTI and household comfort are not the same thing.",
      },
    ],
    sections: [
      {
        title: "The long-tail angle",
        body:
          "A new domain is unlikely to outrank banks for mortgage calculator. It can compete earlier for searches that combine debt payoff, DTI, and home-buying readiness, because the searcher needs a sequence of decisions rather than one isolated number.",
      },
      {
        title: "The right workflow",
        body:
          "Run the credit card payoff calculator first, then rerun debt-to-income after the target card is paid down, then test the mortgage payment. This turns debt payoff from a vague goal into a measurable change in borrowing pressure.",
      },
      {
        title: "The caution",
        body:
          "Paying every dollar to cards can leave no cash for inspection costs, moving, repairs, or emergencies. The strongest answer balances interest savings, credit utilization, cash reserves, and monthly payment capacity.",
      },
    ],
    checklist: [
      "List each card balance, APR, minimum payment, and due date.",
      "Compare avalanche and snowball payoff dates.",
      "Calculate DTI before and after the payoff plan.",
      "Stress-test the mortgage payment with taxes, insurance, and repairs.",
      "Keep cash reserves for closing and moving costs.",
    ],
    faqs: [
      {
        question: "Should I pay off all cards before applying for a mortgage?",
        answer:
          "Not always. Lower balances can help, but cash reserves and payment history also matter. The page should help compare debt reduction with liquidity, not force a one-size answer.",
      },
      {
        question: "Why does DTI matter for SEO content?",
        answer:
          "It is a bridge topic. Users who search debt payoff often also need mortgage affordability, which creates a stronger internal cluster than a standalone calculator page.",
      },
    ],
    sources: [
      {
        label: "Consumer Financial Protection Bureau",
        url: "https://www.consumerfinance.gov/ask-cfpb/what-is-a-debt-to-income-ratio-en-1791/",
        note: "Debt-to-income ratio explanation",
      },
      {
        label: "Consumer Financial Protection Bureau",
        url: "https://www.consumerfinance.gov/consumer-tools/mortgages/",
        note: "Mortgage consumer tools and education",
      },
    ],
  },
  {
    slug: "us-bonus-tax-withholding-cash-plan",
    shortTitle: "US Bonus Tax",
    title: "US Bonus Tax Withholding and Cash-Flow Planning Hub",
    marketCodes: ["US"],
    marketLabel: "United States",
    category: "Tax and Payroll",
    metaDescription:
      "Estimate how a US bonus may be withheld, how it affects take-home cash, and whether it should go to debt, savings, retirement, or taxes.",
    summary:
      "Bonus searches are strong long-tail opportunities because users usually have a precise event in mind: a bonus is coming, the withholding looks confusing, and they need to know what cash will actually remain.",
    searchIntents: [
      "US bonus tax calculator supplemental wages",
      "why was my bonus taxed so much",
      "bonus withholding vs actual tax",
      "what to do with bonus debt savings 401k",
    ],
    calculatorLinks: [
      {
        label: "Bonus Tax Calculator",
        to: "/tools/bonus-tax-calculator",
        description: "Estimate supplemental wage withholding and compare bonus cash scenarios.",
      },
      {
        label: "US Income Tax Calculator",
        to: "/us/tools/income-tax-calculator",
        description: "Review whether withholding is likely to differ from actual federal tax liability.",
      },
      {
        label: "Debt Payoff Calculator",
        to: "/tools/debt-payoff-calculator",
        description: "Test how using a bonus for principal reduction changes payoff timing.",
      },
      {
        label: "Emergency Fund Calculator",
        to: "/tools/emergency-fund-calculator",
        description: "Check whether the bonus should close a cash reserve gap before other goals.",
      },
    ],
    guideLinks: [
      {
        label: "Bonus Tax Withholding Guide",
        to: "/guides/bonus-tax-withholding-guide",
        description: "Explains withholding, actual tax, planning mistakes, and cash allocation.",
      },
      {
        label: "US Paycheck Tax Withholding and 401(k) Guide",
        to: "/us/guides/us-paycheck-tax-withholding-401k-guide",
        description: "Connects bonus planning to regular paycheck and retirement decisions.",
      },
    ],
    sections: [
      {
        title: "Why this page can rank before broad calculators",
        body:
          "Bonus searches are event-driven and specific. A user may search for the exact reason their net bonus looks low. A page that explains supplemental withholding, actual tax, debt payoff, and emergency savings gives more complete intent coverage than a single calculator.",
      },
      {
        title: "Withholding is not always final tax",
        body:
          "A common misunderstanding is treating withholding as the final tax bill. The calculator should show estimated cash withheld, but the guide should explain that final tax depends on the full-year return, deductions, credits, filing status, and other income.",
      },
      {
        title: "Allocation matters",
        body:
          "The best next step depends on the household: high-rate debt, no emergency fund, missed retirement match, upcoming tax bills, or a near-term purchase. The hub should link to calculators that show each tradeoff.",
      },
    ],
    checklist: [
      "Estimate bonus withholding before spending the bonus mentally.",
      "Compare withholding with full-year income tax estimates.",
      "Prioritize late bills and emergency cash before optional spending.",
      "Model debt payoff and 401(k) contributions separately.",
      "Keep documentation for payroll and tax review.",
    ],
    faqs: [
      {
        question: "Is a bonus taxed more than salary?",
        answer:
          "It may be withheld differently, but final tax depends on the full annual tax return. The distinction between withholding and liability should be made clear.",
      },
      {
        question: "What is the best use of a bonus?",
        answer:
          "There is no universal answer. A useful workflow checks emergency reserves, high-interest debt, retirement match, tax obligations, and near-term cash needs.",
      },
    ],
    sources: [
      {
        label: "Internal Revenue Service",
        url: "https://www.irs.gov/publications/p15",
        note: "Employer tax guide and wage withholding context",
      },
      {
        label: "Internal Revenue Service",
        url: "https://www.irs.gov/pub/irs-pdf/p15t.pdf",
        note: "Federal income tax withholding methods",
      },
    ],
  },
  {
    slug: "canada-mortgage-renewal-affordability-stress-test",
    shortTitle: "Canada Mortgage Renewal",
    title: "Canada Mortgage Renewal, Affordability, and Stress-Test Hub",
    marketCodes: ["CA"],
    marketLabel: "Canada",
    category: "Mortgage",
    metaDescription:
      "Plan Canadian mortgage affordability, renewal risk, payment stress tests, down payment, debt service, and household budget capacity.",
    summary:
      "Canadian mortgage searches are not only about buying a home. Many households need to understand renewal shock, payment resets, debt service, and whether the monthly budget survives higher rates.",
    searchIntents: [
      "Canada mortgage renewal payment calculator",
      "Canadian mortgage stress test affordability",
      "mortgage renewal shock Canada budget",
      "rent vs buy Canada mortgage renewal",
    ],
    calculatorLinks: [
      {
        label: "Mortgage Calculator",
        to: "/tools/mortgage-calculator",
        description: "Estimate monthly payment under current and higher rate scenarios.",
      },
      {
        label: "Home Affordability Calculator",
        to: "/tools/home-affordability-calculator",
        description: "Test income, debt, down payment, and housing payment capacity.",
      },
      {
        label: "Rent vs Buy Calculator",
        to: "/tools/rent-vs-buy-calculator",
        description: "Compare ownership cost with renting, cash flexibility, and time horizon.",
      },
      {
        label: "Debt-to-Income Calculator",
        to: "/tools/debt-to-income",
        description: "Review debt pressure before taking or renewing a mortgage.",
      },
    ],
    guideLinks: [
      {
        label: "Canada Mortgage Renewal Affordability Guide",
        to: "/canada/guides/canada-mortgage-renewal-affordability-guide",
        description: "Canadian guide for renewal risk, affordability, payment shock, and budget planning.",
      },
      {
        label: "Canada Rent vs Buy Guide",
        to: "/canada/guides/canada-rent-vs-buy-mortgage-renewal-guide",
        description: "Explains down payment, renewal risk, closing costs, and household flexibility.",
      },
    ],
    sections: [
      {
        title: "Why renewal risk is the entry point",
        body:
          "A new site has a better chance ranking for mortgage renewal and stress-test questions than for generic mortgage calculator. The search is more specific, the user has a real deadline, and the page can connect payment math to household cash flow.",
      },
      {
        title: "The calculation path",
        body:
          "Run the payment at the current rate, then test higher rates, lower income, and extra maintenance cost. Compare the result with monthly debt, savings, childcare, insurance, taxes, and emergency reserves.",
      },
      {
        title: "Local trust angle",
        body:
          "Canadian mortgage content needs Canadian references. The page should point readers to FCAC, Bank of Canada, and CMHC context while making clear that lender approval and legal terms must be checked locally.",
      },
    ],
    checklist: [
      "Calculate the current mortgage payment.",
      "Recalculate at higher renewal rates.",
      "Add property tax, insurance, condo fees, utilities, and maintenance.",
      "Compare against income after other debt payments.",
      "Keep an emergency reserve after closing or renewal costs.",
    ],
    faqs: [
      {
        question: "Why is Canada mortgage renewal a good SEO cluster?",
        answer:
          "It is specific and high-intent. Users are not browsing casually; they need to know whether a payment change will fit their budget.",
      },
      {
        question: "Can this replace lender qualification?",
        answer:
          "No. It is an educational model. Borrowers should verify actual approval, rates, terms, insurance, and local costs with the lender and official resources.",
      },
    ],
    sources: [
      {
        label: "Financial Consumer Agency of Canada",
        url: "https://www.canada.ca/en/financial-consumer-agency/services/mortgages.html",
        note: "Canadian mortgage education",
      },
      {
        label: "Bank of Canada",
        url: "https://www.bankofcanada.ca/rates/interest-rates/",
        note: "Canadian interest-rate data and context",
      },
      {
        label: "Canada Mortgage and Housing Corporation",
        url: "https://www.cmhc-schl.gc.ca/",
        note: "Canadian housing and mortgage market reference",
      },
    ],
  },
  {
    slug: "canada-gst-hst-invoice-pricing-small-business",
    shortTitle: "Canada GST/HST",
    title: "Canada GST/HST Invoice Pricing and Small-Business Cash Hub",
    marketCodes: ["CA"],
    marketLabel: "Canada",
    category: "Business Tax",
    metaDescription:
      "Estimate Canadian GST/HST invoice pricing, tax cash, place-of-supply questions, gross-to-net revenue, and small-business margin pressure.",
    summary:
      "This hub targets Canadian small-business tax math where calculator-only pages are weak. It connects sales tax, VAT-style gross-to-net math, margin, and cash reserve planning.",
    searchIntents: [
      "Canada GST HST invoice calculator",
      "GST HST gross to net business revenue",
      "small business tax cash reserve Canada",
      "place of supply GST HST calculator",
    ],
    calculatorLinks: [
      {
        label: "Sales Tax Calculator",
        to: "/tools/sales-tax-calculator",
        description: "Estimate tax amount, before-tax price, after-tax total, or implied rate.",
      },
      {
        label: "VAT Calculator",
        to: "/tools/vat-calculator",
        description: "Separate gross customer price from net revenue and tax cash.",
      },
      {
        label: "Margin Calculator",
        to: "/tools/margin-calculator",
        description: "Check whether pricing still protects gross margin after tax and costs.",
      },
      {
        label: "Profit Margin Calculator",
        to: "/tools/profit-margin-calculator",
        description: "Review actual profitability after fees, refunds, shipping, and operating costs.",
      },
    ],
    guideLinks: [
      {
        label: "Canada GST/HST Invoice Pricing Guide",
        to: "/canada/guides/canada-gst-hst-invoice-pricing-guide",
        description: "Canadian guide covering place of supply, tax cash, PST interaction, and margins.",
      },
      {
        label: "Margin vs Markup Guide",
        to: "/guides/margin-vs-markup-pricing-guide",
        description: "Explains why price increases and margin targets are often confused.",
      },
    ],
    sections: [
      {
        title: "Why this is more than a tax calculator",
        body:
          "GST/HST searches often begin with rate math, but the real business decision is price, margin, and cash control. A page that separates collected tax from spendable revenue is more valuable than a short rate calculator.",
      },
      {
        title: "Classification before calculation",
        body:
          "The calculator should not pretend to decide place of supply or registration status. The page should tell readers to confirm treatment first, then use the selected rate to calculate invoice totals and tax cash.",
      },
      {
        title: "Margin after tax",
        body:
          "A Canadian seller can collect the right tax and still underprice the sale after card fees, shipping, marketplace costs, refunds, software, and labour. Linking tax and margin calculators makes the page more useful.",
      },
    ],
    checklist: [
      "Confirm place of supply and applicable rate before calculating.",
      "Separate GST/HST collected from net revenue.",
      "Check PST or provincial requirements where relevant.",
      "Calculate margin after shipping, fees, and returns.",
      "Reserve remittance cash before treating revenue as spendable.",
    ],
    faqs: [
      {
        question: "Can the calculator decide my GST/HST rate?",
        answer:
          "No. The user chooses the rate after checking official treatment. The calculator handles the arithmetic once the rate is known.",
      },
      {
        question: "Why include a margin calculator?",
        answer:
          "Because tax compliance does not guarantee profitability. Small businesses also need to know whether the transaction remains profitable after costs.",
      },
    ],
    sources: [
      {
        label: "Canada Revenue Agency",
        url: "https://www.canada.ca/en/revenue-agency/services/tax/businesses/topics/gst-hst-businesses.html",
        note: "GST/HST information for businesses",
      },
      {
        label: "Canada Revenue Agency",
        url: "https://www.canada.ca/en/revenue-agency/services/tax/businesses/topics/gst-hst-businesses/charge-collect-which-rate.html",
        note: "Charging and collecting GST/HST",
      },
    ],
  },
  {
    slug: "canada-tfsa-rrsp-retirement-savings",
    shortTitle: "Canada TFSA/RRSP",
    title: "Canada TFSA, RRSP, Retirement Savings, and Inflation Hub",
    marketCodes: ["CA"],
    marketLabel: "Canada",
    category: "Savings and Retirement",
    metaDescription:
      "Plan Canadian savings and retirement scenarios with TFSA/RRSP context, compound interest, inflation, monthly contributions, and real return assumptions.",
    summary:
      "This hub connects Canadian savings account, TFSA/RRSP planning, retirement contributions, inflation, and future-value calculations into one long-tail content path.",
    searchIntents: [
      "Canada TFSA RRSP retirement savings calculator",
      "monthly savings calculator Canada inflation",
      "RRSP TFSA compound interest projection",
      "Canada retirement savings target calculator",
    ],
    calculatorLinks: [
      {
        label: "Savings Calculator",
        to: "/tools/savings-calculator",
        description: "Estimate how monthly contributions grow over time.",
      },
      {
        label: "Compound Interest Calculator",
        to: "/tools/compound-interest",
        description: "Model compounding from principal, rate, time, and contribution assumptions.",
      },
      {
        label: "Retirement Calculator",
        to: "/tools/retirement-calculator",
        description: "Estimate retirement gaps, target savings, and contribution needs.",
      },
      {
        label: "Inflation Impact Calculator",
        to: "/tools/inflation-impact",
        description: "Convert a future target into real purchasing-power terms.",
      },
    ],
    guideLinks: [
      {
        label: "Canada TFSA/RRSP Retirement Savings Guide",
        to: "/canada/guides/canada-tfsa-rrsp-retirement-savings-guide",
        description: "Canadian guide for savings accounts, retirement contributions, inflation, and real returns.",
      },
      {
        label: "Future Value Guide",
        to: "/guides/future-value-savings-target-guide",
        description: "Shows how savings targets depend on contribution rate, return, and timeline.",
      },
    ],
    sections: [
      {
        title: "Why this cluster can build early traffic",
        body:
          "People search retirement savings with local account names and practical contribution questions. A Canada-specific hub has a better chance than a generic retirement page because it speaks to TFSA, RRSP, inflation, and monthly savings in one place.",
      },
      {
        title: "Real return matters",
        body:
          "A projection should not stop at nominal growth. Inflation changes whether the future balance will buy the same lifestyle, so the savings calculator should be paired with inflation and retirement calculators.",
      },
      {
        title: "No one perfect account answer",
        body:
          "TFSA and RRSP planning depends on income, tax bracket, employer plans, withdrawal timing, debt, emergency savings, and personal risk. The page should explain the variables rather than claim one account is always best.",
      },
    ],
    checklist: [
      "Set the retirement or savings target in today's money.",
      "Translate it into future value using inflation.",
      "Model monthly contributions and realistic return assumptions.",
      "Compare retirement gap and contribution capacity.",
      "Review official account rules before acting.",
    ],
    faqs: [
      {
        question: "Should I use TFSA or RRSP first?",
        answer:
          "It depends on tax bracket, withdrawal plans, employer benefits, debt, and cash-flow needs. The calculator can model the target, not replace personal advice.",
      },
      {
        question: "Why include inflation?",
        answer:
          "A future dollar amount can look large while buying less than expected. Inflation converts the target into a more realistic purchasing-power estimate.",
      },
    ],
    sources: [
      {
        label: "Canada Revenue Agency",
        url: "https://www.canada.ca/en/revenue-agency/services/tax/individuals/topics/rrsps-related-plans.html",
        note: "RRSP and related plan information",
      },
      {
        label: "Canada Revenue Agency",
        url: "https://www.canada.ca/en/revenue-agency/services/tax/individuals/topics/tax-free-savings-account.html",
        note: "TFSA information",
      },
      {
        label: "Bank of Canada",
        url: "https://www.bankofcanada.ca/rates/related/inflation-calculator/",
        note: "Inflation calculator and purchasing-power context",
      },
    ],
  },
  {
    slug: "uk-take-home-pay-pension-salary-sacrifice",
    shortTitle: "UK Take-Home Pay",
    title: "UK Take-Home Pay, Pension, and Salary Sacrifice Hub",
    marketCodes: ["UK"],
    marketLabel: "United Kingdom",
    category: "Salary and Tax",
    metaDescription:
      "Estimate UK take-home pay with income tax, National Insurance, pension contributions, salary sacrifice, raises, and monthly budget planning.",
    summary:
      "This hub targets UK users who need more than a net salary number. It connects salary, tax bands, National Insurance, pension contributions, salary sacrifice, raises, and real monthly spending.",
    searchIntents: [
      "UK take home pay after pension",
      "salary sacrifice calculator UK net pay",
      "UK salary after tax national insurance pension",
      "pay rise take home calculator UK",
    ],
    calculatorLinks: [
      {
        label: "UK Salary Calculator",
        to: "/uk/tools/salary-calculator",
        description: "Estimate UK take-home pay after income tax, National Insurance, and pension assumptions.",
      },
      {
        label: "Salary to Hourly Calculator",
        to: "/tools/salary-to-hourly-calculator",
        description: "Convert salary into an implied hourly rate after hours and work pattern assumptions.",
      },
      {
        label: "Raise Calculator",
        to: "/tools/raise-calculator",
        description: "Compare nominal raises with inflation and take-home planning.",
      },
      {
        label: "Budget Calculator",
        to: "/tools/budget-calculator",
        description: "Turn net monthly pay into a household budget.",
      },
    ],
    guideLinks: [
      {
        label: "UK Take-Home Pay Guide",
        to: "/uk/guides/uk-take-home-pay-tax-guide",
        description: "Explains tax bands, National Insurance, pension contributions, and net salary.",
      },
      {
        label: "UK Mortgage and Pension Affordability Guide",
        to: "/uk/guides/uk-mortgage-pension-affordability-guide",
        description: "Connects net income, pension saving, and housing affordability.",
      },
    ],
    sections: [
      {
        title: "The rankable search angle",
        body:
          "UK salary calculator is competitive. Take-home pay after pension, salary sacrifice, and raise scenarios are more specific. They also let the page give a better answer than a thin calculator because the user needs context.",
      },
      {
        title: "Gross pay is not budget cash",
        body:
          "A salary should be translated into net monthly income after tax, National Insurance, pension, and known deductions. Only then should the user compare rent, mortgage, debt, travel, childcare, and savings.",
      },
      {
        title: "Pension contributions change both sides",
        body:
          "Pension saving can reduce current cash but improve long-term retirement readiness. Salary sacrifice can also affect the calculation. The page should show the tradeoff instead of only saying whether net pay goes up or down.",
      },
    ],
    checklist: [
      "Calculate gross-to-net salary first.",
      "Run pension contribution and salary sacrifice variants.",
      "Compare monthly net pay with housing and debt commitments.",
      "Check whether a raise beats inflation.",
      "Review official tax and National Insurance rules before acting.",
    ],
    faqs: [
      {
        question: "Why does pension reduce take-home pay?",
        answer:
          "Employee contributions usually reduce current spendable cash, although tax treatment and salary sacrifice can change the result. The long-term benefit must be compared with monthly affordability.",
      },
      {
        question: "Can this replace payroll software?",
        answer:
          "No. It is an educational estimate. Payroll, tax code, pension arrangement, benefits, and personal circumstances can change the actual result.",
      },
    ],
    sources: [
      {
        label: "GOV.UK",
        url: "https://www.gov.uk/income-tax-rates",
        note: "Income Tax rates and bands",
      },
      {
        label: "GOV.UK",
        url: "https://www.gov.uk/national-insurance/how-much-you-pay",
        note: "National Insurance overview",
      },
      {
        label: "GOV.UK",
        url: "https://www.gov.uk/employers-workplace-pensions-rules",
        note: "Workplace pension rules and salary sacrifice guidance",
      },
    ],
  },
  {
    slug: "uk-contractor-day-rate-self-assessment-tax-reserve",
    shortTitle: "UK Contractor Day Rate",
    title: "UK Contractor Day Rate, Self Assessment, and Tax Reserve Hub",
    marketCodes: ["UK"],
    marketLabel: "United Kingdom",
    category: "Freelance and Business",
    metaDescription:
      "Calculate UK contractor day rates from desired take-home income, billable days, Self Assessment, National Insurance, pension, expenses, and downtime.",
    summary:
      "This hub targets UK self-employed and contractor searches where users need to convert a salary goal into a day rate while protecting tax reserve, pension, expenses, and slow-month cash.",
    searchIntents: [
      "UK contractor day rate calculator self assessment",
      "self employed tax reserve calculator UK",
      "convert salary to contractor day rate UK",
      "UK freelance rate after tax and expenses",
    ],
    calculatorLinks: [
      {
        label: "Contractor Day Rate Calculator",
        to: "/tools/contractor-day-rate-calculator",
        description: "Convert annual income goals into day-rate pricing using billable days and cost assumptions.",
      },
      {
        label: "Freelance Hourly Rate Calculator",
        to: "/tools/freelance-hourly-rate-calculator",
        description: "Estimate an hourly rate after non-billable time, expenses, taxes, and profit.",
      },
      {
        label: "Budget Calculator",
        to: "/tools/budget-calculator",
        description: "Test whether irregular income supports monthly household needs.",
      },
      {
        label: "Profit Margin Calculator",
        to: "/tools/profit-margin-calculator",
        description: "Review business margin after expenses and taxes.",
      },
    ],
    guideLinks: [
      {
        label: "UK Self-Employed Day Rate Guide",
        to: "/uk/guides/uk-self-employed-day-rate-tax-reserve-guide",
        description: "Detailed UK guide for Self Assessment, tax reserves, National Insurance, and billable days.",
      },
      {
        label: "Cash-Flow Budgeting for Irregular Income",
        to: "/guides/cash-flow-budgeting-irregular-income",
        description: "Shows how variable earners protect baseline expenses and tax money.",
      },
    ],
    sections: [
      {
        title: "Why this cluster matters",
        body:
          "Contractor searches are less saturated than broad salary calculators and often show urgent commercial intent. Users need a rate that protects income, taxes, expenses, unpaid days, and household cash flow.",
      },
      {
        title: "The real calculation",
        body:
          "Start with desired annual personal income, then add tax reserve, National Insurance, pension, professional insurance, software, accountant fees, holidays, sick time, admin, and sales time. Divide by realistic billable days, not calendar workdays.",
      },
      {
        title: "The trust angle",
        body:
          "The page should not claim to file Self Assessment or determine legal employment status. It should explain the planning model and point users toward GOV.UK for tax obligations and deadlines.",
      },
    ],
    checklist: [
      "Estimate annual billable days conservatively.",
      "Add pension, insurance, tax reserve, and business costs.",
      "Convert salary expectations into daily and hourly pricing.",
      "Build a slow-month buffer before raising spending.",
      "Verify Self Assessment and National Insurance obligations.",
    ],
    faqs: [
      {
        question: "Why is my contractor day rate higher than my salary conversion?",
        answer:
          "Because the contractor must price unpaid leave, downtime, business costs, pension, tax reserve, and risk that an employer may normally absorb.",
      },
      {
        question: "Can the calculator tell me my exact tax?",
        answer:
          "No. It models a reserve and rate target. Actual tax depends on income, expenses, legal status, year, and personal circumstances.",
      },
    ],
    sources: [
      {
        label: "GOV.UK",
        url: "https://www.gov.uk/self-assessment-tax-returns",
        note: "Self Assessment overview",
      },
      {
        label: "GOV.UK",
        url: "https://www.gov.uk/national-insurance/how-much-you-pay",
        note: "National Insurance overview",
      },
    ],
  },
  {
    slug: "uk-credit-card-debt-minimum-payment-persistent-debt",
    shortTitle: "UK Card Debt",
    title: "UK Credit Card Minimum Payment and Persistent Debt Hub",
    marketCodes: ["UK"],
    marketLabel: "United Kingdom",
    category: "Debt and Credit",
    metaDescription:
      "Plan UK credit card repayment with APR, minimum payments, persistent debt warnings, fixed extra payments, debt help, and monthly budgets.",
    summary:
      "Use this UK hub to estimate a credit card minimum repayment, compare minimum-only payoff with a fixed monthly plan, understand persistent-debt warning signs, and decide when free debt help should be reviewed.",
    searchIntents: [
      "credit card minimum payment calculator UK",
      "how much is the minimum repayment on a credit card",
      "how long to pay off credit card with minimum payment",
      "persistent debt credit card UK calculator",
      "debt avalanche vs snowball UK credit cards",
      "how long to pay off credit card UK",
    ],
    calculatorLinks: [
      {
        label: "UK Credit Card Minimum Payment Calculator",
        to: "/uk/tools/credit-card-minimum-payment-calculator",
        description: "Estimate a UK minimum repayment and compare minimum-only payoff with a fixed payment.",
      },
      {
        label: "Credit Cards Payoff Calculator",
        to: "/tools/credit-cards-payoff-calculator",
        description: "Create a card payoff schedule with extra payments and repayment methods.",
      },
      {
        label: "Debt Payoff Calculator",
        to: "/tools/debt-payoff-calculator",
        description: "Compare payoff dates and interest cost for a broader debt plan.",
      },
      {
        label: "APR to APY Calculator",
        to: "/tools/apr-to-apy-calculator",
        description: "Understand how a headline APR relates to effective rate comparisons.",
      },
      {
        label: "Budget Calculator",
        to: "/tools/budget-calculator",
        description: "Check whether the extra payment can be sustained after essentials.",
      },
    ],
    guideLinks: [
      {
        label: "UK Credit Card Debt Guide",
        to: "/uk/guides/uk-credit-card-debt-minimum-payment-guide",
        description: "UK guide for APR, minimum payments, persistent debt, repayment order, and debt help.",
      },
      {
        label: "Debt Avalanche vs Snowball Guide",
        to: "/guides/debt-avalanche-vs-snowball",
        description: "Explains the mathematical and behavioral tradeoff between payoff methods.",
      },
    ],
    sections: [
      {
        title: "The rankable question",
        body:
          "UK card debt searches often start with a narrow statement question: how much is the minimum repayment, or how long will minimum payments take? A useful answer has to calculate the next payment and then explain why the minimum can be a weak long-term strategy.",
      },
      {
        title: "How the minimum is usually built",
        body:
          "The exact minimum comes from the card agreement, but many UK cards combine monthly interest, fees or charges, a percentage of the balance, and a fixed pound floor. That is why the calculator lets the user change APR, fees, balance percentage, and floor amount instead of assuming one rule.",
      },
      {
        title: "Budget-first repayment",
        body:
          "The repayment number must fit rent, council tax, utilities, food, transport, and savings. A plan that mathematically saves interest but causes missed priority bills is not a good plan.",
      },
      {
        title: "Persistent debt and advice triggers",
        body:
          "Persistent debt becomes a concern when too much of the repayment is absorbed by interest, fees, and charges instead of principal. If the minimum is unaffordable, balances are not falling, or new borrowing is needed after each payment, the next step may be free debt guidance rather than another payment tweak.",
      },
    ],
    checklist: [
      "Record APR, balance, minimum formula, fixed floor, and due date.",
      "Estimate the first minimum payment before choosing a fixed payment.",
      "Pay priority bills before accelerating unsecured debt.",
      "Compare minimum-only, fixed extra, avalanche, and snowball outcomes.",
      "Review balance-transfer fees and end dates.",
      "Seek free debt guidance if minimum payments are unaffordable.",
    ],
    faqs: [
      {
        question: "How much is the minimum repayment on a UK credit card?",
        answer:
          "It depends on the issuer's terms. A common structure includes interest, fees, a percentage of the balance, and a fixed pound floor. The UK minimum-payment calculator estimates those parts so the user can compare statement-style repayment with a fixed payment.",
      },
      {
        question: "How long will it take to pay off a credit card with minimum payments?",
        answer:
          "Minimum-only payoff can take years because the required payment normally falls as the balance falls. Balance, APR, fees, and the issuer's formula all affect the result.",
      },
      {
        question: "Is persistent debt the same as normal credit card use?",
        answer:
          "No. Persistent debt is a warning that repayment may be too slow compared with charges and interest. Users should review guidance and repayment options early.",
      },
      {
        question: "Should I use avalanche or snowball?",
        answer:
          "Avalanche usually saves more interest. Snowball can help some borrowers maintain consistency by closing smaller balances first.",
      },
    ],
    sources: [
      {
        label: "GOV.UK",
        url: "https://www.gov.uk/options-for-dealing-with-your-debts",
        note: "Official overview of debt options and free debt advice",
      },
      {
        label: "Financial Conduct Authority",
        url: "https://handbook.fca.org.uk/handbook/CONC/6/7.html",
        note: "Credit card minimum repayment and persistent debt rules",
      },
      {
        label: "Financial Conduct Authority",
        url: "https://www.fca.org.uk/publication/documents/helping-customers-in-persistent-debt.pdf",
        note: "Persistent debt repayment expectations",
      },
    ],
  },
  {
    slug: "europe-vat-gross-net-cross-border-pricing",
    shortTitle: "Europe VAT Gross-Net",
    title: "Europe VAT Gross-to-Net, Cross-Border Pricing, and Invoice Cash Hub",
    marketCodes: ["EU", "DE", "FR", "ES"],
    marketLabel: "Europe",
    category: "VAT",
    metaDescription:
      "Calculate European VAT gross-to-net and net-to-gross prices, compare country rates, invoice cash, small-business margin, and cross-border pricing.",
    summary:
      "This hub targets European VAT searches that include practical modifiers such as gross-to-net, net-to-gross, invoice pricing, country rates, and cross-border business context.",
    searchIntents: [
      "Europe VAT gross to net calculator",
      "EU VAT net to gross invoice calculator",
      "VAT inclusive exclusive pricing Europe",
      "cross border VAT price margin calculator",
    ],
    calculatorLinks: [
      {
        label: "VAT Calculator",
        to: "/tools/vat-calculator",
        description: "Add VAT to a net amount or extract VAT from a gross amount using a selected rate.",
      },
      {
        label: "Margin Calculator",
        to: "/tools/margin-calculator",
        description: "Check whether VAT-inclusive pricing protects gross margin.",
      },
      {
        label: "Markup Calculator",
        to: "/tools/markup-calculator",
        description: "Convert cost and target markup into a selling price before or after tax checks.",
      },
      {
        label: "Break-Even Calculator",
        to: "/tools/break-even-calculator",
        description: "Estimate units and revenue required after cost and contribution assumptions.",
      },
    ],
    guideLinks: [
      {
        label: "Europe VAT Cross-Border Pricing Guide",
        to: "/europe/guides/europe-vat-cross-border-pricing-guide",
        description: "European guide for VAT pricing, country checks, invoice cash, and margin planning.",
      },
      {
        label: "VAT-Inclusive vs VAT-Exclusive Pricing",
        to: "/guides/vat-inclusive-vs-exclusive-pricing",
        description: "Explains how to read net price, gross price, and VAT amount.",
      },
      {
        label: "EU SME VAT and Margin Guide",
        to: "/europe/guides/eu-sme-vat-margin-cross-border-pricing-guide",
        description: "Connects VAT treatment with small-business margin and cross-border pricing.",
      },
    ],
    sections: [
      {
        title: "Why VAT is a real opportunity",
        body:
          "VAT keywords are competitive in isolation, but long-tail searches with gross-to-net, invoice, rate, and country modifiers are more realistic. The page should answer how to calculate and how to avoid confusing tax cash with revenue.",
      },
      {
        title: "One formula, local classification",
        body:
          "The arithmetic is simple once the rate is known. The hard part is checking whether the product, service, customer, and country treatment are correct. That is why the page links both calculators and official references.",
      },
      {
        title: "Business margin after VAT",
        body:
          "For businesses, the danger is pricing from the gross receipt. VAT collected may need to be remitted, while fees, refunds, and shipping still reduce margin. Linking VAT and margin tools makes the content less thin.",
      },
    ],
    checklist: [
      "Confirm whether the amount entered is net or gross.",
      "Verify the current VAT rate and product treatment.",
      "Separate VAT from revenue before checking margin.",
      "Review cross-border rules before using the estimate for compliance.",
      "Keep invoice and source documentation.",
    ],
    faqs: [
      {
        question: "Can one VAT calculator cover every European country?",
        answer:
          "It can calculate from a selected rate, but it cannot decide legal treatment. Users should verify rate and classification with official national or EU resources.",
      },
      {
        question: "Why does gross-to-net VAT matter?",
        answer:
          "Because a gross receipt includes tax. Businesses need to separate tax from usable revenue before measuring margin or cash flow.",
      },
    ],
    sources: [
      {
        label: "European Commission",
        url: "https://taxation-customs.ec.europa.eu/taxation/vat_en",
        note: "EU VAT rules and rates overview",
      },
      {
        label: "GOV.UK",
        url: "https://www.gov.uk/guidance/rates-of-vat-on-different-goods-and-services",
        note: "UK VAT rates and product classification reference",
      },
    ],
  },
  {
    slug: "euro-area-mortgage-rate-risk-affordability",
    shortTitle: "Euro Mortgage Risk",
    title: "Euro Area Mortgage Rate Risk and Affordability Hub",
    marketCodes: ["EU", "DE", "FR", "ES"],
    marketLabel: "Europe",
    category: "Mortgage",
    metaDescription:
      "Stress-test euro area mortgage payments with ECB rate context, fixed versus variable assumptions, loan-to-value, debt pressure, and household budgets.",
    summary:
      "This hub targets euro area mortgage searches that need more than payment math. It frames the mortgage as an affordability, creditworthiness, rate-risk, and household budget question.",
    searchIntents: [
      "euro mortgage rate risk calculator",
      "ECB rate mortgage payment stress test",
      "Europe mortgage affordability calculator",
      "fixed vs variable mortgage payment Europe",
    ],
    calculatorLinks: [
      {
        label: "Mortgage Calculator",
        to: "/tools/mortgage-calculator",
        description: "Estimate payment under different loan, rate, and term assumptions.",
      },
      {
        label: "Loan-to-Value Calculator",
        to: "/tools/loan-to-value-calculator",
        description: "Calculate equity, deposit pressure, and refinancing flexibility.",
      },
      {
        label: "Debt-to-Income Calculator",
        to: "/tools/debt-to-income",
        description: "Screen monthly debt pressure against gross income.",
      },
      {
        label: "Budget Calculator",
        to: "/tools/budget-calculator",
        description: "Check whether a stress-tested payment fits the household.",
      },
    ],
    guideLinks: [
      {
        label: "Euro Area Mortgage Rate Risk Guide",
        to: "/europe/guides/euro-area-mortgage-rate-risk-creditworthiness-guide",
        description: "EU guide for ECB context, fixed versus variable risk, creditworthiness, and stress tests.",
      },
      {
        label: "Germany Mortgage Affordability Guide",
        to: "/de/guides/germany-mortgage-affordability-interest-fixation-guide",
        description: "Germany-specific guide for affordability, interest fixation, and household budgets.",
      },
      {
        label: "Spain Mortgage Prepayment and Euribor Guide",
        to: "/es/guides/spain-mortgage-prepayment-euribor-guide",
        description: "Spain-specific guide for Euribor-linked risk and prepayment checks.",
      },
    ],
    sections: [
      {
        title: "The rankable angle",
        body:
          "A pan-European mortgage calculator is broad, but ECB rate risk, fixed versus variable payments, and country-specific stress tests create long-tail search paths. The hub links those paths instead of spreading them thinly.",
      },
      {
        title: "Affordability is local",
        body:
          "The loan formula is universal, but taxes, fees, underwriting, borrower protection, energy costs, insurance, and wage patterns differ. The page should provide a stress-test method and send users to country-specific guides.",
      },
      {
        title: "Payment risk and liquidity risk",
        body:
          "A borrower can pass a payment estimate and still be fragile if all savings go into the deposit. Mortgage content should connect monthly payment, LTV, DTI, and emergency cash in one flow.",
      },
    ],
    checklist: [
      "Estimate payment at the offered rate.",
      "Recalculate with a higher rate and lower income case.",
      "Check LTV and cash left after deposit.",
      "Add local taxes, insurance, utilities, and maintenance.",
      "Verify lender and country-specific rules locally.",
    ],
    faqs: [
      {
        question: "Can this predict ECB rates?",
        answer:
          "No. It stress-tests what payment changes would do to the household. Rate forecasts are uncertain and should not be treated as guarantees.",
      },
      {
        question: "Why include country guides?",
        answer:
          "Because mortgage rules and costs differ across Europe. The hub gives the workflow; country guides add local context.",
      },
    ],
    sources: [
      {
        label: "European Central Bank",
        url: "https://www.ecb.europa.eu/stats/html/index.en.html",
        note: "Euro area statistics and rate context",
      },
      {
        label: "European Banking Authority",
        url: "https://www.eba.europa.eu/regulation-and-policy/consumer-protection",
        note: "Consumer protection and creditworthiness context",
      },
    ],
  },
  {
    slug: "germany-vat-freelancer-net-salary",
    shortTitle: "Germany VAT and Net Salary",
    title: "Germany VAT, Freelancer Pricing, Net Salary, and Household Budget Hub",
    marketCodes: ["DE"],
    marketLabel: "Germany",
    category: "Tax and Freelance",
    metaDescription:
      "Plan German VAT, freelancer hourly pricing, net salary, emergency savings, mortgage affordability, and household budget scenarios.",
    summary:
      "This Germany hub creates a local search path around VAT, freelancer pricing, net salary, emergency savings, and mortgage affordability without pretending the site provides official tax advice.",
    searchIntents: [
      "Germany VAT freelancer invoice calculator",
      "German net salary household budget calculator",
      "Germany freelance hourly rate VAT tax reserve",
      "Germany mortgage affordability interest fixation",
    ],
    calculatorLinks: [
      {
        label: "VAT Calculator",
        to: "/tools/vat-calculator",
        description: "Calculate VAT-inclusive or VAT-exclusive German invoice examples using a selected rate.",
      },
      {
        label: "Freelance Hourly Rate Calculator",
        to: "/tools/freelance-hourly-rate-calculator",
        description: "Estimate pricing after billable hours, costs, tax reserve, and downtime.",
      },
      {
        label: "Budget Calculator",
        to: "/tools/budget-calculator",
        description: "Translate income assumptions into monthly household capacity.",
      },
      {
        label: "Mortgage Calculator",
        to: "/tools/mortgage-calculator",
        description: "Stress-test German mortgage payment scenarios.",
      },
    ],
    guideLinks: [
      {
        label: "Germany Freelancer VAT Hourly Rate Guide",
        to: "/de/guides/germany-freelancer-vat-hourly-rate-guide",
        description: "Germany-specific guide for VAT-aware freelancer pricing and reserves.",
      },
      {
        label: "Germany Net Salary Household Budget Guide",
        to: "/de/guides/germany-net-salary-household-budget-guide",
        description: "Connects German net salary assumptions to household budgeting.",
      },
      {
        label: "Germany Mortgage Affordability Guide",
        to: "/de/guides/germany-mortgage-affordability-interest-fixation-guide",
        description: "Local guide for interest fixation, payment risk, and affordability.",
      },
    ],
    sections: [
      {
        title: "Why country depth matters",
        body:
          "Germany-specific searches are better than generic Europe pages because users include local terms like VAT, net salary, freelancer, and mortgage rate fixation. The hub gathers those paths in one crawlable place.",
      },
      {
        title: "Arithmetic with source limits",
        body:
          "The calculators can model VAT, pricing, salary budget, and mortgage payments. They cannot determine every legal tax position, insurance contribution, local charge, or lender rule, so source checks and disclaimers must stay visible.",
      },
      {
        title: "The practical sequence",
        body:
          "A freelancer should price invoices after VAT and non-billable time. A household should convert net income into a budget before mortgage calculations. Both workflows become stronger when calculators and guides are linked together.",
      },
    ],
    checklist: [
      "Use a Germany-specific guide before relying on a global calculator.",
      "Separate VAT from spendable revenue.",
      "Model freelancer rates using billable hours and reserves.",
      "Translate net salary into monthly budget capacity.",
      "Verify official tax, insurance, and lender rules.",
    ],
    faqs: [
      {
        question: "Can this page calculate exact German tax?",
        answer:
          "No. It organizes educational calculators and guides. Users should verify legal and tax outcomes with official German sources or a qualified professional.",
      },
      {
        question: "Why connect VAT and freelancer pricing?",
        answer:
          "Because collected VAT and client revenue are not the same. A freelancer needs to price after tax cash, costs, and non-billable time.",
      },
    ],
    sources: [
      {
        label: "Bundesministerium der Finanzen",
        url: "https://www.bundesfinanzministerium.de/Web/DE/Themen/Steuern/Steuerarten/Umsatzsteuer/umsatzsteuer.html",
        note: "German VAT information",
      },
      {
        label: "Deutsche Bundesbank",
        url: "https://www.bundesbank.de/en/statistics/money-and-capital-markets/interest-rates-and-yields/housing-loans-to-households-mortgage-loans-secured-by-residential-real-estate-621950",
        note: "German mortgage-rate statistics",
      },
    ],
  },
  {
    slug: "france-spain-tva-iva-credit-mortgage",
    shortTitle: "France and Spain VAT/Credit",
    title: "France TVA, Spain IVA, Consumer Credit, and Mortgage Planning Hub",
    marketCodes: ["FR", "ES", "EU"],
    marketLabel: "France and Spain",
    category: "VAT and Credit",
    metaDescription:
      "Compare France TVA and Spain IVA planning with consumer credit cost, TAEG/APR, mortgage affordability, inflation, and household budgets.",
    summary:
      "This hub creates local depth for France and Spain while keeping the architecture efficient: VAT and invoice math, consumer credit cost, mortgage affordability, and budget resilience are linked instead of isolated.",
    searchIntents: [
      "France TVA invoice calculator freelance",
      "Spain IVA invoice pricing calculator",
      "France TAEG consumer credit repayment calculator",
      "Spain Euribor mortgage prepayment calculator",
    ],
    calculatorLinks: [
      {
        label: "VAT Calculator",
        to: "/tools/vat-calculator",
        description: "Calculate gross-to-net and net-to-gross VAT/TVA/IVA examples from selected rates.",
      },
      {
        label: "Loan Repayment Calculator",
        to: "/tools/loan-repayment-calculator",
        description: "Estimate credit payments, interest, and repayment timelines.",
      },
      {
        label: "Mortgage Payoff Calculator",
        to: "/tools/mortgage-payoff-calculator",
        description: "Test how extra payments change mortgage payoff and interest cost.",
      },
      {
        label: "Inflation Impact Calculator",
        to: "/tools/inflation-impact",
        description: "Review purchasing-power pressure for savings and household budgets.",
      },
    ],
    guideLinks: [
      {
        label: "France Freelancer TVA Hourly Rate Guide",
        to: "/fr/guides/france-freelancer-tva-hourly-rate-guide",
        description: "France-specific guide for TVA, pricing, hourly rates, and reserves.",
      },
      {
        label: "France Consumer Credit TAEG Guide",
        to: "/fr/guides/france-consumer-credit-repayment-taeg-guide",
        description: "Explains TAEG, credit repayment, and borrowing-capacity checks.",
      },
      {
        label: "Spain IVA Invoice Pricing Guide",
        to: "/es/guides/spain-freelancer-iva-invoice-pricing-guide",
        description: "Spain-specific guide for IVA invoice pricing and freelance cash planning.",
      },
      {
        label: "Spain Euribor Mortgage Guide",
        to: "/es/guides/spain-mortgage-prepayment-euribor-guide",
        description: "Spain-specific guide for Euribor-linked mortgage risk and prepayment planning.",
      },
    ],
    sections: [
      {
        title: "Why combined local hubs help",
        body:
          "France and Spain each need local pages, but a combined European comparison hub helps Google understand that the site covers VAT, credit cost, and mortgage planning across specific markets instead of publishing disconnected pages.",
      },
      {
        title: "TVA and IVA are not only rate math",
        body:
          "The calculators can add or extract tax, but the value is in explaining invoice cash, pricing, reserves, and margin. Readers should still verify local tax treatment from official sources.",
      },
      {
        title: "Credit cost needs local language",
        body:
          "France searches often involve TAEG, while Spain mortgage searches often include Euribor. Using those terms in useful context gives the site a better long-tail target than generic loan pages.",
      },
    ],
    checklist: [
      "Use local terminology in page titles and anchor text.",
      "Link VAT/TVA/IVA pages to margin and cash-flow calculators.",
      "Connect consumer credit cost to repayment and budget planning.",
      "Link Spain mortgage pages to Euribor and prepayment content.",
      "Verify official tax and banking references before relying on results.",
    ],
    faqs: [
      {
        question: "Why combine France and Spain in one hub?",
        answer:
          "The hub helps users and crawlers see the European pattern while still linking to country-specific France and Spain guides.",
      },
      {
        question: "Can this replace local financial advice?",
        answer:
          "No. It is educational planning content. Tax, consumer-credit, and mortgage decisions should be verified with official sources or qualified professionals.",
      },
    ],
    sources: [
      {
        label: "impots.gouv.fr",
        url: "https://www.impots.gouv.fr/professionnel/tva",
        note: "French TVA information",
      },
      {
        label: "Agencia Tributaria",
        url: "https://sede.agenciatributaria.gob.es/Sede/iva.html",
        note: "Spanish IVA information",
      },
      {
        label: "Banco de Espana",
        url: "https://clientebancario.bde.es/pcb/en/menu-horizontal/productosservici/financiacion/prestamopersonal/",
        note: "Spanish personal loan and credit information",
      },
    ],
  },
];

export const getTopicClusterPath = (slug: string) => `/topics/${slug}`;

export const getTopicClusterBySlug = (slug?: string) =>
  SEO_TOPIC_CLUSTERS.find((cluster) => cluster.slug === slug);

export const getTopicClustersForMarket = (marketCode?: MarketCode) => {
  if (!marketCode || marketCode === "Global") return SEO_TOPIC_CLUSTERS;
  return SEO_TOPIC_CLUSTERS.filter((cluster) => cluster.marketCodes.includes(marketCode));
};
